import { Logo } from '@/components/Header';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-5 flex items-center justify-between">
          <Logo />
          <a href="/" className="flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-stone-400 mb-12">Last updated: January 1, 2026</p>

        <div className="prose prose-stone max-w-none space-y-8 text-stone-600 leading-relaxed text-[15px]">
          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">1. Introduction</h2>
            <p>SalesWall AI ("we", "us", "our") is committed to protecting the privacy and personal data of our users in compliance with the General Data Protection Regulation (EU) 2016/679 ("GDPR"), the ePrivacy Directive 2002/58/EC, and all applicable EU member state data protection laws.</p>
            <p>This Privacy Policy explains how we collect, use, store, and protect your personal data when you visit our website at saleswall.ai or use our services.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">2. Data Controller</h2>
            <p>The data controller responsible for your personal data is:</p>
            <p className="bg-stone-50 rounded-lg p-4 border border-stone-100">
              SalesWall AI<br />
              Email: <a href="mailto:saleswall@saleswall.ai" className="text-violet-600 hover:text-violet-700 underline underline-offset-4">saleswall@saleswall.ai</a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">3. Personal Data We Collect</h2>
            <p>We collect the following categories of personal data:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li><strong>Contact Information:</strong> Email address when you join our waitlist or contact us.</li>
              <li><strong>Usage Data:</strong> IP address, browser type, pages visited, time and date of access, collected via cookies and similar technologies.</li>
              <li><strong>Communication Data:</strong> Any information you provide when contacting us directly.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">4. Legal Basis for Processing</h2>
            <p>Under Article 6 of the GDPR, we process your personal data based on the following legal grounds:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li><strong>Consent (Art. 6(1)(a)):</strong> When you voluntarily submit your email to join our waitlist.</li>
              <li><strong>Legitimate Interest (Art. 6(1)(f)):</strong> To operate and improve our website, ensure security, and communicate relevant updates.</li>
              <li><strong>Legal Obligation (Art. 6(1)(c)):</strong> To comply with applicable laws and regulations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">5. How We Use Your Data</h2>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>To process and manage your waitlist registration.</li>
              <li>To communicate with you about early access and product updates.</li>
              <li>To improve our website and services.</li>
              <li>To comply with legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">6. Data Retention</h2>
            <p>We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected. Waitlist data is retained until you request deletion or until the waitlist is no longer active. You may request deletion at any time by contacting us.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">7. Your Rights Under GDPR</h2>
            <p>As a data subject in the EU/EEA, you have the following rights:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li><strong>Right of Access (Art. 15):</strong> Obtain a copy of the personal data we hold about you.</li>
              <li><strong>Right to Rectification (Art. 16):</strong> Request correction of inaccurate data.</li>
              <li><strong>Right to Erasure (Art. 17):</strong> Request deletion of your personal data ("right to be forgotten").</li>
              <li><strong>Right to Restrict Processing (Art. 18):</strong> Request limitation of how we use your data.</li>
              <li><strong>Right to Data Portability (Art. 20):</strong> Receive your data in a structured, machine-readable format.</li>
              <li><strong>Right to Object (Art. 21):</strong> Object to processing based on legitimate interest.</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time without affecting the lawfulness of prior processing.</li>
            </ul>
            <p className="mt-3">To exercise any of these rights, contact us at <a href="mailto:saleswall@saleswall.ai" className="text-violet-600 hover:text-violet-700 underline underline-offset-4">saleswall@saleswall.ai</a>. We will respond within 30 days.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">8. Data Transfers</h2>
            <p>If your data is transferred outside the EU/EEA, we ensure appropriate safeguards are in place, including Standard Contractual Clauses (SCCs) approved by the European Commission, in compliance with Chapter V of the GDPR.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">9. Data Security</h2>
            <p>We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction, including encryption, access controls, and regular security assessments.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">10. Supervisory Authority</h2>
            <p>If you believe your data protection rights have been violated, you have the right to lodge a complaint with your local Data Protection Authority (DPA) in your EU/EEA member state.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">12. Contact Us</h2>
            <p>For any questions regarding this Privacy Policy or your personal data, please contact us at:</p>
            <p className="bg-stone-50 rounded-lg p-4 border border-stone-100">
              <a href="mailto:saleswall@saleswall.ai" className="text-violet-600 hover:text-violet-700 underline underline-offset-4">saleswall@saleswall.ai</a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
